import { useCallback, useEffect, useMemo, useState } from "react"
import { useInterval } from "usehooks-ts"

import {
  fetchAllBorrowData,
  fetchMusdCollateralPrice,
  fetchMusdLiquityProps,
  selectHasAccess,
  selectHasLoadedAccountData,
  selectLiquityProps,
  selectUserLoan,
  selectWalletAddress,
  setIsLoadingBorrowData,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { BALANCE_UPDATE_INTERVAL } from "#/shared/constants"
import {
  bigIntToHumanReadableFormat,
  separateThousandsWithComma,
} from "#/shared/utils"

export const useBorrowInterestRate = () => {
  const userLoan = useDappSelector(selectUserLoan)

  const interestRateLabel = useMemo(
    () => bigIntToHumanReadableFormat((userLoan?.interestRate ?? 0n) * 100n, 2),
    [userLoan],
  )

  return { interestRate: userLoan?.interestRate ?? 0n, interestRateLabel }
}

export const useBorrowData = () => {
  const address = useDappSelector(selectWalletAddress)
  const hasAccess = useDappSelector(selectHasAccess)

  const [lastFetchedAddress, setLastFetchedAddress] = useState<string | null>(
    null,
  )

  const hasLoadedAccountData = useDappSelector(selectHasLoadedAccountData)

  const dispatch = useDappDispatch()

  const fetchMusdDataHandler = useCallback(async () => {
    if (address !== lastFetchedAddress) {
      dispatch(setIsLoadingBorrowData(true))
    }

    await dispatch(fetchMusdCollateralPrice())
    await dispatch(fetchMusdLiquityProps())

    if (!address && !hasLoadedAccountData) {
      dispatch(setIsLoadingBorrowData(false))
    }

    if (!address || !hasAccess) {
      dispatch(setIsLoadingBorrowData(false))
      return
    }

    await dispatch(fetchAllBorrowData())

    dispatch(setIsLoadingBorrowData(false))
    setLastFetchedAddress(address)
  }, [address, lastFetchedAddress, dispatch, hasAccess, hasLoadedAccountData])

  useEffect(() => {
    const fetchMusdData = async () => {
      await fetchMusdDataHandler()
    }

    fetchMusdData()
  }, [dispatch, fetchMusdDataHandler])

  useInterval(fetchMusdDataHandler, BALANCE_UPDATE_INTERVAL)
}

// When we have a total amount that was borrowed we can calculate the loan amount without fees like this:
// loan = (loanWithFees - gas) / (1 + borrowingRate)
export const useDebtAmountWithoutFees = () => {
  const userLoan = useDappSelector(selectUserLoan)
  const liquityProps = useDappSelector(selectLiquityProps)

  // TODO: review if we can just use principal amount from the contract
  const loanAmount = useMemo(() => {
    if (!userLoan || !liquityProps) return 0n

    const { gasCompensation, borrowingFeeRate } = liquityProps

    const noFeeAmount =
      ((userLoan.debtAmount - gasCompensation) * 10n ** 18n) /
      (borrowingFeeRate + 10n ** 18n)

    return noFeeAmount
  }, [userLoan, liquityProps])

  const loanAmountLabel = useMemo(
    () =>
      separateThousandsWithComma(bigIntToHumanReadableFormat(loanAmount), 2),
    [loanAmount],
  )

  const debtAmountWithoutGasComp = useMemo(() => {
    if (!userLoan || !liquityProps) return 0n

    return userLoan.debtAmount - (liquityProps.gasCompensation ?? 0n)
  }, [userLoan, liquityProps])

  const debtAmountWithoutGasCompLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(debtAmountWithoutGasComp),
        2,
      ),
    [debtAmountWithoutGasComp],
  )

  return {
    loanAmount,
    loanAmountLabel,
    debtAmountWithoutGasComp,
    debtAmountWithoutGasCompLabel,
  }
}
